
import { useState } from 'react';
import { useInventory } from './hooks/useInventory';
import { StockTransfer, InventoryItem } from './types';
import { useForm } from 'react-hook-form';
import { supabase } from '@/lib/supabase';

export default function TransferStock() {
  const { transfers, items, createTransfer, updateTransfer } = useInventory();
  const { register, handleSubmit, reset, watch } = useForm();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedItems, setSelectedItems] = useState<InventoryItem[]>([]);

  const watchedFromBranch = watch('fromBranch');

  const onSubmit = async (data: any) => {
    try {
      const transferData = {
        ...data,
        id: crypto.randomUUID(),
        items: selectedItems,
        requestedBy: 'Current User',
        status: 'pending',
        requestDate: new Date().toISOString().split('T')[0]
      };
      
      // Direct Supabase insert
      const { error } = await supabase
        .from('inventory_transfers')
        .insert(transferData);
        
      if (error) throw error;
      
      reset();
      setSelectedItems([]);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create transfer:', error);
    }
  };

  const handleStatusUpdate = async (transferId: string, newStatus: string) => {
    try {
      await updateTransfer.mutateAsync({
        id: transferId,
        data: { 
          status: newStatus as StockTransfer['status'],
          ...(newStatus === 'approved' && { approvedBy: 'Current User' }),
          ...(newStatus === 'in-transit' && { transferDate: new Date().toISOString() })
        }
      });
    } catch (error) {
      console.error('Failed to update transfer status:', error);
    }
  };

  const filteredTransfers = transfers.data?.filter((transfer: StockTransfer) => {
    return filterStatus === 'all' || transfer.status === filterStatus;
  }) || [];

  const availableItems = items.data?.filter((item: InventoryItem) => 
    item.status === 'in-stock' && item.branch === watchedFromBranch
  ) || [];

  if (transfers.isLoading) return <div className="p-6">Loading transfers...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-primary">Inter-Branch Transfer</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          New Transfer
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="in-transit">In Transit</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Quick Transfer Form */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold text-primary mb-4">Quick Transfer</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="grid md:grid-cols-4 gap-4">
          <input 
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
            placeholder="Item Code" 
            {...register('itemCode', { required: true })} 
          />
          <select 
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
            {...register('fromBranch', { required: true })}
          >
            <option value="">From Branch</option>
            <option value="Main Store">Main Store</option>
            <option value="Mall Branch">Mall Branch</option>
            <option value="City Center">City Center</option>
            <option value="Warehouse">Warehouse</option>
          </select>
          <select 
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
            {...register('toBranch', { required: true })}
          >
            <option value="">To Branch</option>
            <option value="Main Store">Main Store</option>
            <option value="Mall Branch">Mall Branch</option>
            <option value="City Center">City Center</option>
            <option value="Warehouse">Warehouse</option>
          </select>
          <button 
            type="submit"
            className="bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Transfer
          </button>
        </form>
      </div>

      {/* Transfer Records */}
      <div className="space-y-4">
        {filteredTransfers.map((transfer: StockTransfer) => (
          <div key={transfer.id} className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-primary">
                  {transfer.fromBranch} → {transfer.toBranch}
                </h3>
                <p className="text-sm text-gray-600">
                  Requested by {transfer.requestedBy} on {new Date(transfer.requestDate).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  transfer.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                  transfer.status === 'in-transit' ? 'bg-purple-100 text-purple-800' :
                  transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {transfer.status}
                </span>
                {transfer.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStatusUpdate(transfer.id, 'approved')}
                      className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(transfer.id, 'rejected')}
                      className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap"
                    >
                      Reject
                    </button>
                  </div>
                )}
                {transfer.status === 'approved' && (
                  <button
                    onClick={() => handleStatusUpdate(transfer.id, 'in-transit')}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700 transition-colors whitespace-nowrap"
                  >
                    Mark In Transit
                  </button>
                )}
                {transfer.status === 'in-transit' && (
                  <button
                    onClick={() => handleStatusUpdate(transfer.id, 'completed')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap"
                  >
                    Mark Completed
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Items ({transfer.items?.length || 0})</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {transfer.items?.map((item: InventoryItem, index: number) => (
                    <div key={index} className="text-sm text-gray-600 flex justify-between">
                      <span>{item.name}</span>
                      <span className="font-mono">{item.code}</span>
                    </div>
                  )) || <p className="text-sm text-gray-500">No items specified</p>}
                </div>
              </div>
              
              {transfer.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-600">{transfer.notes}</p>
                </div>
              )}
            </div>

            {transfer.approvedBy && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Approved by {transfer.approvedBy}
                  {transfer.transferDate && ` on ${new Date(transfer.transferDate).toLocaleDateString()}`}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Transfer Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">Create Detailed Transfer</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Branch</label>
                  <select
                    {...register('fromBranch', { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select Branch</option>
                    <option value="Main Store">Main Store</option>
                    <option value="Mall Branch">Mall Branch</option>
                    <option value="City Center">City Center</option>
                    <option value="Warehouse">Warehouse</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Branch</label>
                  <select
                    {...register('toBranch', { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select Branch</option>
                    <option value="Main Store">Main Store</option>
                    <option value="Mall Branch">Mall Branch</option>
                    <option value="City Center">City Center</option>
                    <option value="Warehouse">Warehouse</option>
                  </select>
                </div>
              </div>

              {watchedFromBranch && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Items ({availableItems.length})
                  </label>
                  <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                    {availableItems.map((item: InventoryItem) => (
                      <label key={item.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(i => i.id === item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, item]);
                            } else {
                              setSelectedItems(selectedItems.filter(i => i.id !== item.id));
                            }
                          }}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="font-medium">{item.name}</span>
                            <span className="text-sm text-gray-500">{item.code}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {item.category} • {item.grossWeight}g • ₹{item.value.toLocaleString()}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Add any additional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={!selectedItems.length}
                  className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Create Transfer
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
